import React, { useEffect, useState } from "react";
import styles from "./Statistics.module.scss";
import { fetchOrderForStatistics } from "../../store/order-actions";
import SimpleModal from "../../components/UI/Modals/SimpleModal/SimpleModal";
import Checkbox from "../../components/UI/Checkbox/Checkbox";
import { registerLocale } from "react-datepicker";
import sr from "date-fns/locale/sr-Latn";
import Calendar from "react-calendar";
import moment from "moment";
import filterIll from "../../assets/icons/filter-ilustration.svg";
import { ReactComponent as ArrowIcon } from "../../assets/icons/arrow-icon.svg";
import { ReactComponent as RFZOIcon } from "../../assets/icons/rfzo-icon.svg";
import Button from "../../components/UI/Buttons/Button/Button";
import classNames from "classnames";
registerLocale("sr", sr);
type Props = {};

interface AggregatedItem {
  name: string;
  availabilityCode?: string;
  totalQuantity: number;
  totalPrice: number;
}

interface UserDeliveryResponse {
  id: number;
  firstName: string;
  secondName: string;
  phoneNumber: string;
  personalId: string;
  personalIdType?: string;
  username?: string;
  nickname?: string;
  userPhoto?: string;
  bankAccount?: string;
  locale?: string;
  permitAdvertising?: boolean;
  privacyPolicy?: boolean;
  privacyPolicyForAssociation?: boolean;
  userLoyaltyPoints?: number;
  lbo?: string;
}

interface TransactionResponse {
  id: number;
  orderNumber: number;
  deliveryType?: string;
  userDeliveryResponse?: UserDeliveryResponse;
  firstName: string;
  secondName: string;
  userPhoneNumber: string;
  userLBO?: string;
  orderCreated: string;
  orderProcessed?: string;
  orderPrepared?: string;
  created: string;
  status: string;
  totalPrice: number;
  totalPriceWithoutDiscount: number;
  items: any[];
  marketplaceResponse?: any;
  noteForUser?: string;
  noteForCanceledStatus?: string;
  noteForMarketplace?: string;
  userAddressResponse?: any;
  rating?: any;
}

function Statistics({}: Props) {
  const [list, setList] = useState<any[]>([]);

  const [showFilters, setShowFilters] = useState(false);

  const [filters, setFilters] = useState<any>({
    createdFrom: moment()
      .subtract(1, "month")
      .startOf("day")
      .format("YYYY-MM-DDTHH:mm"),
    createdTo: moment().format("YYYY-MM-DDTHH:mm"),
    status: "TAKEN,CANCELED,REJECTED,SUSPENDED",
  });

  useEffect(() => {
    if (filters.status === "") {
      setList([]);
    } else {
      fetchOrderForStatistics(filters).then((res: any) => setList(res.data));
    }
  }, [filters]);

  const aggregateItems = (orders: any[]): AggregatedItem[] => {
    const itemMap = new Map<string, AggregatedItem>();

    orders.forEach((order) => {
      const isRFZOOrder = !!order.userLBO;

      order.items?.forEach((item: any) => {
        const key = `${item.itemResponse.name}${
          item.itemResponse.itemAvailabilityCode || ""
        }`;

        // For RFZO orders, use unitPriceWithoutDiscount; for regular orders, use price
        const itemPrice = isRFZOOrder
          ? item.unitPriceWithoutDiscount || 0
          : item.price || 0;

        if (itemMap.has(key)) {
          const existing = itemMap.get(key)!;
          existing.totalQuantity += item.quantity;
          existing.totalPrice += itemPrice * item.quantity;
        } else {
          itemMap.set(key, {
            name: item.itemResponse.name,
            availabilityCode: item.itemResponse.itemAvailabilityCode,
            totalQuantity: item.quantity,
            totalPrice: itemPrice * item.quantity,
          });
        }
      });
    });

    return Array.from(itemMap.values()).sort(
      (a, b) => b.totalQuantity - a.totalQuantity
    );
  };

  const renderTimestamps = (order: TransactionResponse) => {
    const timestamps = [
      {
        label: "Kreirana porudžbina",
        timestamp: order.orderCreated,
        className: styles.timestampCreated,
      },
      {
        label: "Obrađena porudžbina",
        timestamp: order.orderProcessed,
        className: styles.timestampProcessed,
      },
      {
        label: "Završena transakcija",
        timestamp: order.created,
        className: styles.timestampCompleted,
      },
    ];

    return (
      <div className={styles.timestamps}>
        {timestamps
          .filter((item) => item.timestamp) // Only show timestamps that exist
          .map((item, index) => (
            <div
              key={index}
              className={`${styles.timestampRow} ${item.className}`}
            >
              <div className={styles.timestampLabel}>{item.label}:</div>
              <div className={styles.timestampValue}>
                {moment(item.timestamp).format("DD.MM.YYYY. HH:mm")}
              </div>
            </div>
          ))}
      </div>
    );
  };

  const renderOrderDetails = (order: TransactionResponse) => {
    const isRFZOOrder = !!order.userLBO;

    return (
      <div className={`${styles.orderDetail}`}>
        <div className={`${styles.orderDetailInfo}`}>
          <div className={styles.orderDetailHeader}>
            <div>
              <div className={styles.pharmacyInfo}>
                Apoteka: {order.marketplaceResponse?.name},{" "}
                {order.marketplaceResponse?.location}
              </div>
            </div>
          </div>

          <div className={styles.customerInfo}>
            <div className={styles.customerName}>
              {order.firstName} {order.secondName}
            </div>
            <div className={styles.customerPhone}>{order.userPhoneNumber}</div>
            {/* {order.deliveryType && (
              <div className={styles.deliveryType}>
                {order.deliveryType === "FAST_DELIVERY" && "Brza dostava"}
                {order.deliveryType === "STANDARD_DELIVERY" &&
                  "Standardna dostava"}
                {order.deliveryType === "PICK_UP_BY_FRIEND" &&
                  "Preuzima prijatelj"}
                {order.deliveryType === "PENSION_CARD_DELIVERY" &&
                  "Dostava - penziona kartica"}
              </div>
            )} */}
          </div>
          {renderTimestamps(order)}
        </div>

        <div className={styles.orderItems}>
          {order.items.map((item: any, idx: number) => {
            // For RFZO orders, show full price before subsidy
            const displayPrice = isRFZOOrder
              ? item.unitPriceWithoutDiscount
              : item.price;

            return (
              <div key={idx} className={styles.orderItem}>
                <span className={styles.orderItemName}>
                  {item.quantity}x {item.itemResponse.name}
                  {item.itemResponse.itemAvailabilityCode &&
                    ` ${item.itemResponse.itemAvailabilityCode}`}
                </span>
                <span className={styles.orderItemPrice}>
                  {displayPrice ? displayPrice.toFixed(2) : "0.00"} RSD
                </span>
              </div>
            );
          })}
        </div>

        <div className={styles.orderNotes}>
          {order.noteForUser && (
            <div className={`${styles.orderNote} ${styles.orderNoteForUser}`}>
              <div>
                <strong>Napomena farmaceuta:</strong>
              </div>
              <div>{order.noteForUser}</div>
            </div>
          )}

          {order.noteForCanceledStatus && (
            <div
              className={`${styles.orderNote} ${styles.orderNoteForCanceledStatus}`}
            >
              <div>
                <strong>Razlog otkazivanja:</strong>
              </div>
              <div>{order.noteForCanceledStatus}</div>
            </div>
          )}

          {order.noteForMarketplace && (
            <div
              className={`${styles.orderNote} ${styles.orderNoteForMarketplace}`}
            >
              <div>
                <strong>Napomena korisnika:</strong>
              </div>
              <div>{order.noteForMarketplace}</div>
            </div>
          )}

          {order.userDeliveryResponse && (
            <div className={styles.deliveryInfo}>
              <div>
                <strong>Dostavljač:</strong>
              </div>
              <div className={styles.deliveryPersonName}>
                {order.userDeliveryResponse.firstName}{" "}
                {order.userDeliveryResponse.secondName}
              </div>
              <div className={styles.deliveryPersonPhone}>
                Telefon: {order.userDeliveryResponse.phoneNumber}
              </div>
              {order.userDeliveryResponse.personalId && (
                <div className={styles.deliveryPersonId}>
                  Lični dokument: {order.userDeliveryResponse.personalId}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className={styles.statisticsWrap}>
      <div className={styles.filtersIcon} onClick={() => setShowFilters(true)}>
        <img src={filterIll} alt="filter" />
      </div>
      {showFilters && (
        <Filters
          hideModal={() => setShowFilters(false)}
          filters={filters}
          setFilters={setFilters}
        />
      )}
      <div className={styles.dataGrid}>
        <div className={styles.dataGridHeader}>
          <div className={styles.dataGridHeaderItem}>Broj porudžbine</div>
          <div className={styles.dataGridHeaderItem}>Ukupna cena</div>
          <div className={styles.dataGridHeaderItem}>Status</div>
        </div>
        <ul className={styles.dataGridBody}>
          {list.length === 0 && (
            <li className={styles.noResults}>
              Nema rezultata za izabrane filtere
            </li>
          )}
          {list.map((order, index) => (
            <li className={styles.dataGridBodyItem} key={index}>
              <details>
                <summary
                  id={`order-${order.id}`}
                  aria-controls={`order-${order.id}-content`}
                  className={styles.dataGridBodyItemSummary}
                  title="Detalji"
                >
                  <div
                    className={`${styles.dataGridBodyItemValue} ${styles.orderId}`}
                  >
                    <span className={styles.orderId}>#{order.orderNumber}</span>
                    <span className={styles.orderDate}>
                      {moment(order.orderCreated || order.created).format(
                        "DD.MM.YYYY."
                      )}
                    </span>
                    {order.userLBO && <RFZOIcon title="RFZO" />}
                  </div>
                  <div
                    className={`${styles.dataGridBodyItemValue} ${styles.totalPrice}`}
                  >
                    {order.status !== "TAKEN" &&
                      order.status !== "IN_DELIVERY" &&
                      "-"}
                    {(order.userLBO
                      ? order.totalPriceWithoutDiscount
                      : order.totalPrice
                    ).toFixed(2)}{" "}
                    RSD
                  </div>
                  <div
                    className={classNames(
                      styles.dataGridBodyItemValue,
                      styles.status,
                      {
                        [styles.statusTaken]:
                          order.status === "TAKEN" ||
                          order.status === "IN_DELIVERY",
                        [styles.statusCanceled]: order.status === "CANCELED",
                        [styles.statusRejected]: order.status === "REJECTED",
                        [styles.statusSuspended]: order.status === "SUSPENDED",
                      }
                    )}
                  >
                    {statusMapping[order.status]}{" "}
                    <ArrowIcon className={styles.arrowIcon} />
                  </div>
                </summary>
                <div
                  id={`order-${order.id}-content`}
                  aria-labelledby={`order-${order.id}`}
                  className={styles.dataGridBodyItemContent}
                >
                  {renderOrderDetails(order)}
                </div>
              </details>
            </li>
          ))}
        </ul>
        <div className={styles.dataGridFooter}>
          <details>
            <summary
              id="dataGridFooterSummary"
              className={styles.dataGridFooterSummary}
              title="Detalji"
            >
              <div className={styles.dataGridFooterItem}>Ukupno:</div>
              <div className={styles.dataGridFooterItem}>
                {list
                  .reduce((acc, curr) => {
                    const orderPrice = curr.userLBO
                      ? curr.totalPriceWithoutDiscount
                      : curr.totalPrice;

                    // Only TAKEN and IN_DELIVERY orders count as positive revenue
                    // All other statuses (REJECTED, CANCELED, SUSPENDED) count as negative
                    return curr.status === "TAKEN" ||
                      curr.status === "IN_DELIVERY"
                      ? acc + orderPrice
                      : acc - orderPrice;
                  }, 0)
                  ?.toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}{" "}
                RSD
              </div>
              <div className={styles.dataGridFooterItem}>
                {list.length} porudžbine{" "}
                <ArrowIcon className={styles.arrowIcon} />
              </div>
            </summary>
            <div
              aria-labelledby="dataGridFooterSummary"
              className={styles.aggregatedItems}
            >
              {/* <div className={styles.aggregatedItemsHeader}>
                <h4>Ukupno naručeni proizvodi:</h4>
              </div> */}
              <ul className={styles.aggregatedItemsList}>
                {aggregateItems(list).map((item, index) => (
                  <li key={index} className={styles.aggregatedItem}>
                    <span className={styles.aggregatedItemName}>
                      {item.totalQuantity}x {item.name}
                      {item.availabilityCode && ` ${item.availabilityCode}`}
                    </span>
                    <span className={styles.aggregatedItemPrice}>
                      {item.totalPrice.toFixed(2)} RSD
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </details>
        </div>
      </div>
    </div>
  );
}

export default Statistics;

const STATUSES = ["TAKEN", "REJECTED", "CANCELED", "SUSPENDED"];

export const Filters = ({
  filters,
  setFilters,
  hideModal,
  showDate = true,
}: any) => {
  const filteringStatuses = (status: string) => {
    let currentStatuses = filters?.status ? filters.status.split(",") : [];

    if (currentStatuses.includes(status)) {
      currentStatuses = currentStatuses.filter((s: string) => s !== status);
    } else {
      currentStatuses.push(status);
      currentStatuses = currentStatuses.filter((s: string) => s !== "ALL");
    }

    setFilters({
      ...filters,
      status: currentStatuses.filter(Boolean).join(","),
    });
  };

  const selectAll = () => {
    setFilters({ ...filters, status: "TAKEN,CANCELED,REJECTED,SUSPENDED" });
  };
  const deSelectAll = () => {
    setFilters({ ...filters, status: "" });
  };

  return (
    <SimpleModal
      className={styles.filtersModal}
      backdropClickHandler={hideModal}
      show={false}
    >
      <div className={styles.segment}>
        <div className={styles.header}>Status</div>
        <div className={styles.statusFilter}>
          <div className={styles.header}>Prikazi</div>
          {STATUSES.map((status) => (
            <div key={status} className={styles.selectItem}>
              <Checkbox
                value={
                  filters?.status?.includes(status) || filters?.status === "ALL"
                }
                onChange={() => {
                  filteringStatuses(status);
                }}
              />
              {/* @ts-ignore */}
              {STATUS_LABELS[status]}
            </div>
          ))}
          <div className={styles.selectItem}>
            <Checkbox
              value={filters?.status?.split(",").length === 4}
              onChange={() =>
                filters?.status?.split(",").length === 4
                  ? deSelectAll()
                  : selectAll()
              }
            />
            Svi
          </div>
        </div>
      </div>
      {showDate && (
        <div className={styles.segment}>
          <div className={styles.header}>Period</div>
          <Calendar
            selectRange={true}
            returnValue="range"
            value={[
              moment(filters?.createdFrom).toDate(),
              moment(filters?.createdTo).toDate(),
            ]}
            onChange={(val) => {
              if (val && Array.isArray(val)) {
                const from = moment(val[0])
                  .startOf("day")
                  .format("YYYY-MM-DDTHH:mm");
                const to = moment(val[1])
                  .endOf("day")
                  .format("YYYY-MM-DDTHH:mm");
                setFilters({
                  ...filters,
                  createdFrom: from,
                  createdTo: to,
                });
              }
            }}
            maxDate={new Date()}
            locale="sr"
            className={styles.calendarWrap}
          />
        </div>
      )}
      <Button
        buttonType="blue"
        onClick={() => {
          hideModal();
        }}
      >
        Primeni
      </Button>
    </SimpleModal>
  );
};

const STATUS_LABELS = {
  TAKEN: "Preuzeto",
  REJECTED: "Odbijeno",
  CANCELED: "Otkazano",
  SUSPENDED: "Propušteno",
  ALL: "Svi",
};

interface Statuses {
  [key: string]: string;
}

const statusMapping: Statuses = {
  CREATED: "Kreirano",
  REQUESTED: "Zatrazeno",
  PREPARED: "Pripremljeno",
  PROCESSED: "Obradjeno",
  TAKEN: "Preuzeto",
  IN_DELIVERY: "Preuzeto",
  REJECTED: "Odbijeno",
  CANCELED: "Otkazano",
  SUSPENDED: "Propušteno",
  REQUEST_EXPIRED: "Propušteno",
  PROCESSED_WITHOUT_CONTENT: "Odbijeno",
  REJECT_REQUESTED: "Odbijeno",
};
